// src/pages/AI/Pilot.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://myboq.in";
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || "myBOQ";

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const Pilot = ({ openContactModal }) => {
  useEffect(() => {
    track("ai_pilot_view", { path: "/ai/pilot" });
  }, []);

  const requestPilot = (placement = "ai_pilot") => {
    track("ai_pilot_request_click", { placement, intent: "ai_pilot" });
    if (openContactModal) openContactModal();
  };

  return (
    <main className="bg-white">
      <SEO
        title={`${WEBSITE_NAME} | AI Pilot Program`}
        description="Join the myBOQ AI Pilot Program. Help shape verification-friendly estimation automation for MH PWD SSR workflows and public infrastructure outputs."
        keywords="myBOQ AI pilot, estimation automation pilot, SSR estimation AI, government estimation software pilot, verification friendly outputs"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={`${BASE_URL}/ai/pilot`}
      />

      {/* HERO */}
      <section className="px-5 sm:px-6 lg:px-20 pt-10 pb-10">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-500">
            <Link
              to="/"
              className="hover:text-orange-600"
              onClick={() => track("nav_click", { item: "home", from: "ai_pilot_breadcrumb" })}
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link
              to="/ai"
              className="hover:text-orange-600"
              onClick={() => track("nav_click", { item: "ai_overview", from: "ai_pilot_breadcrumb" })}
            >
              AI &amp; Innovation
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700 font-semibold">Pilot Program</span>
          </div>

          <div className="mt-4 grid lg:grid-cols-12 gap-10 items-start">
            {/* Left */}
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
                AI Pilot Program (Practical, Workflow-First)
              </h1>

              <p className="mt-4 text-gray-700 text-base sm:text-lg leading-relaxed">
                The myBOQ AI Pilot is designed for early adopters who want faster estimation while
                keeping outputs compliant and verification-friendly. Pilot partners help us validate:
                <span className="font-semibold text-gray-900">
                  item discovery, quality checks, and assisted workflows
                </span>
                .
              </p>

              <p className="mt-3 text-gray-600">
                Focus: Maharashtra (MH PWD SSR) today • Multi-state roadmap planned.
              </p>

              {/* CTAs */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => requestPilot("ai_pilot_hero")}
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
                >
                  Apply for Pilot
                </button>

                <Link
                  to="/ai/roadmap"
                  className="inline-flex items-center justify-center rounded-full bg-gray-100 px-6 py-3 text-gray-900 font-semibold hover:bg-gray-200 transition"
                  onClick={() => track("nav_click", { item: "ai_roadmap", placement: "ai_pilot_hero" })}
                >
                  View AI Roadmap
                </Link>
              </div>

              {/* Chips */}
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "Early access features",
                  "Pilot feedback loop",
                  "Verification-friendly outputs",
                  "Practical workflows only",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="text-xs sm:text-sm px-3 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-100"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* Right card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                    <span className="text-xl" aria-hidden="true">
                      🧪
                    </span>
                  </div>

                  <div>
                    <div className="text-gray-900 font-extrabold text-lg">Who should join</div>
                    <p className="text-gray-600 text-sm mt-1">
                      Teams who create/verify estimates often and want faster approvals with fewer errors.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {[
                    "PWD / municipal teams",
                    "Consultants & reviewers",
                    "Contractors with volume work",
                    "Institutions & trainers",
                  ].map((item) => (
                    <div key={item} className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                      <span className="text-gray-900 font-semibold">✔</span>{" "}
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 text-xs text-gray-500">
                  Pilot access is limited to ensure quality onboarding and feedback cycles.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILOT STRUCTURE */}
      <section className="px-5 sm:px-6 lg:px-20 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">How the Pilot Works</h2>
            <div className="text-sm text-gray-500">Simple steps • Clear outcomes</div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6 shadow-sm">
              <h3 className="text-lg font-extrabold text-gray-900">1) Apply & shortlist</h3>
              <p className="mt-2 text-sm text-gray-700">
                Share your workflow context (department/firm/institution) and typical estimate types.
              </p>
              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Use-case fit check</li>
                <li>Data & workflow understanding</li>
                <li>Pilot scope confirmation</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-extrabold text-gray-900">2) Onboarding & setup</h3>
              <p className="mt-2 text-sm text-gray-700">
                We configure the pilot workflow and give a guided walkthrough for your team.
              </p>
              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Pilot account setup</li>
                <li>Workflow mapping</li>
                <li>Team enablement session</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-extrabold text-gray-900">3) Use & evaluate</h3>
              <p className="mt-2 text-sm text-gray-700">
                Your team uses pilot features during real estimation activities and provides feedback.
              </p>
              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Weekly feedback cycle</li>
                <li>Quality / speed tracking</li>
                <li>Iteration updates</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-extrabold text-gray-900">4) Pilot outcome & rollout</h3>
              <p className="mt-2 text-sm text-gray-700">
                We finalize learnings and align on next rollout steps for your org or institution.
              </p>
              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Outcome summary</li>
                <li>Recommended adoption path</li>
                <li>Roadmap prioritization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DARK CTA FOOTER */}
      <section className="px-5 sm:px-6 lg:px-20 pb-14">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-[#0B1B3A] text-white p-7 sm:p-10 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-extrabold">Apply for the AI Pilot</h2>

            <p className="mt-3 text-white/80 leading-relaxed">
              If you want early access and a direct feedback loop with the product team, apply for the pilot.
              We’ll prioritize partners who can validate real workflows.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => requestPilot("ai_pilot_footer")}
                className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
              >
                Apply Now
              </button>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-white/10 px-6 py-3 text-white font-semibold hover:bg-white/20 transition"
                onClick={() => track("contact_click", { placement: "ai_pilot_footer" })}
              >
                Contact myBOQ →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Pilot;